import { useState } from 'react';
import { BadgePercent, Check, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { MoneyValue } from './MoneyValue';
import { useValidateCoupon } from '@/hooks/mutations';
import { useCartStore } from '@/stores/cart.store';

const ERROR_COPY: Record<string, string> = {
  COUPON_INVALID: 'Kode kupon tidak valid.',
  COUPON_EXPIRED: 'Kupon sudah kedaluwarsa.',
  COUPON_LIMIT: 'Batas pemakaian kupon tercapai.',
  COUPON_NOT_APPLICABLE: 'Kupon tidak berlaku untuk pesanan ini.',
};

export function CouponField() {
  const [code, setCode] = useState('');
  const couponResult = useCartStore((s) => s.couponResult);
  const applyCoupon = useCartStore((s) => s.applyCoupon);
  const { mutate, isPending } = useValidateCoupon();

  const submit = () => {
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) return;
    mutate(trimmed, {
      onSuccess: (result) => {
        if (result.valid) {
          applyCoupon(result);
          toast.success(`Kupon ${result.code} diterapkan.`);
        } else {
          const copy = result.errorCode ? ERROR_COPY[result.errorCode] : undefined;
          toast.error(copy ?? result.message ?? 'Kupon tidak valid.');
        }
      },
      onError: (err) => toast.error(err.message),
    });
  };

  const clear = () => {
    applyCoupon(null);
    setCode('');
  };

  if (couponResult?.valid) {
    return (
      <div className="space-y-1.5">
        <Label>Kupon</Label>
        <div className="flex items-center justify-between rounded-md border-2 border-success/40 bg-success/5 px-4 py-2.5">
          <div className="flex items-center gap-2">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-success/15 text-success">
              <Check className="h-4 w-4" />
            </span>
            <div>
              <p className="font-display font-semibold text-ink-900">{couponResult.code}</p>
              {typeof couponResult.discountAmount === 'number' && (
                <p className="text-xs text-success">
                  Hemat <MoneyValue value={couponResult.discountAmount} className="text-xs text-success" />
                </p>
              )}
            </div>
          </div>
          <Button variant="ghost" size="icon" className="h-9 w-9" onClick={clear} aria-label="Hapus kupon">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <Label htmlFor="coupon">Kode kupon</Label>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <BadgePercent className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" />
          <Input
            id="coupon"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === 'Enter' && submit()}
            placeholder="Masukkan kode"
            className="pl-9 uppercase"
            autoComplete="off"
          />
        </div>
        <Button variant="outline" onClick={submit} disabled={isPending || !code.trim()}>
          {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Terapkan'}
        </Button>
      </div>
    </div>
  );
}
